<template>
  <GlobalAlert
    v-if="!appStore.hideNavigation && state?.navigation?.alert"
    :alert="state?.navigation.alert"
  />
  <header class="header">
    <div class="content">
      <div class="left">
        <div
          v-if="!appStore.hideNavigation"
          class="hamburger"
        >
          <NavigationItemsBurgerIcon @toggle-menu="toggleMenu" />
          <div v-if="menuOpen">
            <GeneralPopup @close="toggleMenu">
              <div
                v-if="state.navigation.logo"
                class="logo"
              >
                <NuxtLink
                  :to="`/${locale}`"
                  @click="handlePageChange()"
                >
                  <NuxtImg
                    provider="storyblok"
                    :src="state.navigation.logo.filename"
                    :alt="state.navigation.logo.alt"
                  />
                </NuxtLink>
              </div>
              <div
                v-if="state.navigation.mobileMenuItems && !appStore.hideNavigation"
                class="menu"
              >
                <div
                  v-for="(menuItem, i) in state.navigation.mobileMenuItems"
                  :key="i"
                  class="item"
                >
                  <p
                    v-if="i < state.navigation.mobileMenuItems.length - 1"
                    class="body-3"
                  >
                    {{ menuItem.title }}
                  </p>

                  <Link
                    v-else-if="menuItem.redirect"
                    :blok="menuItem.redirect"
                    @click="handlePageChange(menuItem.redirect)"
                  >
                    <p class="body-3 body-4">
                      {{ menuItem.title }}
                    </p>
                  </Link>
                  <div
                    v-if="menuItem.items"
                    class="submenu"
                  >
                    <template
                      v-for="(submenuItem, j) in menuItem.items"
                      :key="j"
                    >
                      <Link
                        v-if="submenuItem.redirect"
                        :blok="submenuItem.redirect"
                        class="item body-4"
                        @click="handlePageChange(submenuItem.redirect)"
                      >
                        {{ submenuItem.label }}
                      </Link>
                    </template>
                  </div>
                </div>
              </div>
            </GeneralPopup>
          </div>
        </div>
        <div
          v-if="state.navigation.logo"
          class="logo"
        >
          <NuxtLink
            :to="`/${locale}`"
            @click="handlePageChange()"
          >
            <NuxtImg
              provider="storyblok"
              :src="state.navigation.logo.filename"
              :alt="state.navigation.logo.alt"
            />
          </NuxtLink>
        </div>
      </div>
      <div class="right">
        <div
          v-if="state.navigation.desktopMenuItems && !appStore.hideNavigation"
          class="menu"
        >
          <template
            v-for="(menuItem, i) in state.navigation.desktopMenuItems"
            :key="i"
          >
            <GeneralElementsDropdown
              v-if="menuItem.items?.length > 1"
              :label="menuItem.title"
              :items="menuItem.items"
              :option-wrap="false"
              class="desktop"
              @select="menuDropdownItemClick"
            />
            <Link
              v-else-if="menuItem.redirect"
              :blok="menuItem.redirect"
              class="item desktop"
              @click="handlePageChange(menuItem.redirect)"
            >
              {{ menuItem.title }}
            </Link>
          </template>

          <Link
            v-if="state.mobMenuJobs.redirect"
            :blok="state.mobMenuJobs.redirect"
            class="item mobile"
            @click="handlePageChange(state.mobMenuJobs.redirect)"
          >
            <p>
              {{ state.mobMenuJobs.label }}
            </p>
          </Link>
          <NavigationItemsLanguageSelector />
        </div>
      </div>
    </div>
  </header>
</template>

<script lang="ts" setup>
  import { head, last } from 'lodash-es'
  import { useAppStore } from '~/stores'
  import { useFetchHeader } from '~/composables/useFetchHeader'
  import type ILink from '~/types/Storyblok/Generic/Link'
  import Link from '~/storyblok/Generic/Link.vue'
  import GlobalAlert from '~/components/Navigation/GlobalAlert.vue'

  const appStore = useAppStore()
  const { locale } = useI18n()
  const { push: routerPush } = useRouter()
  const menuOpen = ref(false)
  const { fetchHeader } = useFetchHeader()

  const state = reactive<any>({
    navigation: {},
    mobMenuJobs: {}
  })

  fetchHeader(locale.value).then((response) => {
    state.navigation = response
    const menuEnd: any = last(response.mobileMenuItems)

    state.mobMenuJobs = head(menuEnd?.items)
  })

  watch(locale, () => {
    fetchHeader(locale.value).then((response) => {
      state.navigation = response
      const menuEnd: any = last(response.mobileMenuItems)
      state.mobMenuJobs = head(menuEnd?.items)
    })
  })

  function toggleMenu() {
    appStore.noScroll = !appStore.noScroll
  }

  watch(
    () => appStore.noScroll,
    () => {
      updateBodyClass()
    }
  )

  const updateBodyClass = () => {
    const body = document.body

    appStore.noScroll ? body.classList.add('no-scroll') : body.classList.remove('no-scroll')

    menuOpen.value = appStore.noScroll
  }

  function menuDropdownItemClick(value: any) {
    handlePageChange(value.redirect)
    const path = formatSbPath(value.redirect.story.full_slug, locale.value)
    routerPush(`/${locale.value}${path}`)
  }

  function handlePageChange(link?: ILink | undefined) {
    appStore.noScroll = false

    const slug = link ? link.story.full_slug : ''
    const path = formatSbPath(slug, locale.value)

    GTM.headerPageChange(`/${locale.value}${path}`)
  }
</script>

<style lang="scss" scoped>
  @import '~/assets/styles/common.scss';

  .header {
    background-color: rgba($white, 0.9);
    box-shadow: 0px 4px 6px rgba(106, 119, 142, 0.2);
    padding: 0 rem(16);
    position: relative;
    z-index: 10;

    @include tablet-up {
      padding: rem(20) rem(60);
    }

    .mobile {
      display: block;

      @include tablet-up {
        display: none;
      }
    }

    .desktop {
      display: none;

      @include tablet-up {
        display: block;
      }
    }

    .content {
      align-items: center;
      display: flex;
      justify-content: space-between;
      margin: auto;
      max-width: 1120px;
    }

    .left {
      display: flex;
      align-items: center;
      gap: 16px;

      .logo {
        text-align: right;

        img {
          height: 32px;

          @include tablet-up {
            height: 48px;
          }

          @include desktop-up {
            height: 64px;
          }
        }
      }

      .hamburger {
        @include tablet-up {
          display: none;
        }
      }

      .menu {
        // TODO: Slide in/out animation
        overflow-y: auto;
        max-height: calc(100vh - 70px);

        > .item {
          &:last-child {
            border-top: 1px solid $bt-green;
            padding-top: rem(16);

            p {
              color: $bt-blue;
            }
          }

          p {
            color: $border-grey;
            margin: 0;
            padding: 0 0 rem(16);
          }
        }

        .submenu {
          display: flex;
          flex-direction: column;

          .item {
            color: $bt-blue;
            display: block;
            margin: 0 0 rem(16) rem(24);
          }
        }
      }
    }

    .right {
      flex-grow: 1;

      .menu {
        align-items: center;
        display: flex;
        flex-direction: row;
        justify-content: flex-end;

        @include tablet-up {
          gap: 16px;
        }

        .item {
          color: $bt-blue;
          padding: rem(12);
        }
      }
    }
  }
</style>
